const DEFAULT_ASSET_CDN_BASE_URL = "https://assets.genlp.ai";

const STORAGE_HOST_MATCHERS = [
  "storage.googleapis.com",
  "firebasestorage.googleapis.com",
  "firebasestorage.app",
  "appspot.com",
];

const isAbsoluteHttpUrl = (value: string) => /^https?:\/\//i.test(value);
const ASSET_PATH_PREFIXES = [
  "sites/",
  "hosts/",
  "demo-content/",
  "screenshots/",
  "images/",
  "social-media-assets/",
  "uploads/",
];
const ASSET_PATH_EXTENSIONS = /\.(?:png|jpe?g|webp|gif|svg|mp4|webm|mp3|wav|json|pdf|ico|css|js|map)$/i;

const looksLikeAssetPath = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("#") || trimmed.startsWith("data:")) return false;
  if (/\s/.test(trimmed)) return false;
  if (ASSET_PATH_PREFIXES.some((prefix) => trimmed.startsWith(prefix))) return true;
  if (ASSET_PATH_EXTENSIONS.test(trimmed)) return true;
  return trimmed.includes("/");
};

export const isProductionEnv = () => import.meta.env.PROD === true;

export const getAssetCdnBaseUrl = () => {
  const raw = (
    import.meta.env.VITE_ASSET_CDN_BASE_URL ||
    import.meta.env.ASSET_CDN_BASE_URL ||
    ""
  ) as string;
  let base = raw.trim() || DEFAULT_ASSET_CDN_BASE_URL;
  if (raw && !/^https?:\/\//i.test(raw)) {
    base = `https://${raw}`;
  }
  return base.replace(/\/+$/, "");
};

export const getAssetCdnHost = () => {
  try {
    return new URL(getAssetCdnBaseUrl()).host.toLowerCase();
  } catch {
    return "assets.genlp.ai";
  }
};

export const getStorageHostMatchers = () => {
  const cdnHost = getAssetCdnHost();
  return cdnHost ? [...STORAGE_HOST_MATCHERS, cdnHost] : [...STORAGE_HOST_MATCHERS];
};

export const isStorageHostedUrl = (url?: string | null) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  const matchers = getStorageHostMatchers();
  try {
    const host = new URL(url).hostname.toLowerCase();
    return matchers.some((matcher) => host.includes(matcher));
  } catch {
    return matchers.some((matcher) => lower.includes(matcher));
  }
};

export const normalizeAssetPath = (value: string) => {
  if (!value) return value;
  let path = value.replace(/^\/+/, "");
  try {
    path = decodeURIComponent(path);
  } catch {
    // Ignore decode errors; return raw path.
  }
  return path.replace(/^\/+/, "");
};

export const extractStoragePathFromUrl = (value: string): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("gs://")) {
    const remainder = trimmed.slice(5);
    const parts = remainder.split("/");
    parts.shift();
    return normalizeAssetPath(parts.join("/"));
  }

  // Only process absolute HTTP URLs from known storage hosts
  // Do NOT process relative paths - they could be anything (tag names, CSS values, etc.)
  if (!isAbsoluteHttpUrl(trimmed)) {
    return null;
  }

  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    const pathname = url.pathname || "";
    // Skip URLs already on our CDN - they're already in the correct format
    const cdnHost = getAssetCdnHost();
    if (cdnHost && host.includes(cdnHost)) {
      return null;
    }

    if (host === "storage.googleapis.com") {
      const parts = pathname.split("/").filter(Boolean);
      if (parts.length >= 2) {
        parts.shift();
        return normalizeAssetPath(parts.join("/"));
      }
      return null;
    }

    if (host.endsWith(".storage.googleapis.com")) {
      return normalizeAssetPath(pathname);
    }

    if (host.includes("firebasestorage.googleapis.com") || host.includes("firebasestorage.app")) {
      const idx = pathname.indexOf("/o/");
      if (idx >= 0) {
        const encoded = pathname.slice(idx + 3);
        return normalizeAssetPath(encoded);
      }
    }
  } catch {
    return null;
  }

  return null;
};

export const buildCdnUrl = (filePath: string) => {
  const base = getAssetCdnBaseUrl();
  const normalized = normalizeAssetPath(filePath);
  return normalized ? `${base}/${encodeURI(normalized)}` : base;
};

export const buildStoragePublicUrl = (filePath: string, bucketName?: string) => {
  const bucket = bucketName || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  if (!bucket) return null;
  const normalized = normalizeAssetPath(filePath);
  return `https://storage.googleapis.com/${bucket}/${encodeURI(normalized)}`;
};

export const normalizeAssetUrlForPersistence = (value?: string | null) => {
  if (value == null || typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!isProductionEnv()) return value;
  const path = extractStoragePathFromUrl(trimmed);
  if (!path) return value;
  return buildCdnUrl(path);
};

export const resolveAssetUrl = (
  filePath?: string | null,
  fallbackUrl?: string | null,
  options?: { bucketName?: string }
) => {
  const primary = typeof filePath === "string" ? filePath.trim() : "";
  const fallback = typeof fallbackUrl === "string" ? fallbackUrl.trim() : "";
  const candidate = primary || fallback || "";
  if (!candidate) return null;

  if (isProductionEnv()) {
    if (primary && !isAbsoluteHttpUrl(primary)) {
      return looksLikeAssetPath(primary) ? buildCdnUrl(primary) : primary;
    }
    if (fallback && !isAbsoluteHttpUrl(fallback)) {
      return looksLikeAssetPath(fallback) ? buildCdnUrl(fallback) : fallback;
    }
    return candidate;
  }

  if (fallback && isAbsoluteHttpUrl(fallback)) return fallback;
  if (isAbsoluteHttpUrl(candidate)) return candidate;
  if (!looksLikeAssetPath(candidate)) return candidate;
  return buildStoragePublicUrl(candidate, options?.bucketName) || candidate;
};
